import React, { useState, useEffect } from 'react';
import {
  Box,
  Icon,
  Text,
  SimpleGrid,
  useColorModeValue,
  Flex,
  VStack,
  Badge,
  Spinner,
  HStack,
  Button,
  Divider,
  Switch,
  FormControl,
  FormLabel,
  FormHelperText,
  useToast
} from "@chakra-ui/react";
import { Link } from 'react-router-dom';
import { getBusinessSlug } from '../../../utils/slugify';
// Custom components
import MiniStatistics from "../../../components/card/MiniStatistics";
import IconBox from "../../../components/icons/IconBox";
import { MdOutlineSell, MdShoppingBasket, MdOutlineLocalOffer, MdArrowForward, MdMonetizationOn } from "react-icons/md";
import ComplexTable from "../../../views/admin/default/components/ComplexTable";
import DailyTraffic from "../../../views/admin/default/components/DailyTraffic";
import Tasks from "../../../views/admin/default/components/Tasks";
import GettingStartedChecklist from "../../../components/GettingStartedChecklist";
import { orderService } from '../../../services/orderService';
import io from 'socket.io-client';
import api from '../../../services/api';
import Card from "../../../components/card/Card";
import { campaignService } from "../../../services/campaignService";
import { pricingService } from "../../../services/pricingService";
import { restaurantService } from "../../../services/restaurantService";
import PremiumUpgradeModal from "../../../components/modals/PremiumUpgradeModal";
import { authService } from "../../../services/authService";
import dayjs from "dayjs";

export default function UserReports() {
  const brandColor = useColorModeValue("brand.500", "white");
  const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
  const [activeOrders, setActiveOrders] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({
    dailyOrders: 0,
    deliveredItems: 0,
    dailyEarnings: 0
  });
  const [loading, setLoading] = useState(true);
  const [orderStats, setOrderStats] = useState({
    data: {
      activeOrders: 0,
      deliveredOrders: 0,
      cancelledOrders: 0
    }
  });
  const [selectedTimeRange, setSelectedTimeRange] = useState('today');
  const [trafficData, setTrafficData] = useState({
    labels: [],
    data: []
  });
  const [businessHours, setBusinessHours] = useState(null);
  const [restaurantStats, setRestaurantStats] = useState({
    averageRating: 0,
    totalRatings: 0,
    followersCount: 0,
    followersGrowth: 0,
    rankScore: 0
  });
  const [activeCampaigns, setActiveCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const [campaigns, setCampaigns] = useState([]);
  const [campaignStats, setCampaignStats] = useState({
    total: 0,
    active: 0,
    expired: 0,
    upcoming: 0
  });
  const [userPlan, setUserPlan] = useState('STANDARD');
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [menomCoinsEnabled, setMenomCoinsEnabled] = useState(true); // Default true
  const [waiterCalls, setWaiterCalls] = useState([]);
  const toast = useToast();
  const currentUser = authService.getCurrentUser();
  const isStaff = currentUser?.role === 'staff' && currentUser?.userType === 'employee';

  const fetchDashboardData = async (timeRange = 'today') => {
    try {
      setLoading(true);
      const [ordersResponse, statsResponse] = await Promise.all([
        orderService.getActiveOrders(),
        orderService.getDashboardStats(timeRange)
      ]);

      if (ordersResponse?.data) {
        setActiveOrders(ordersResponse.data);
      }

      if (statsResponse?.data) {
        const stats = statsResponse.data;
        
        setDashboardStats({
          dailyOrders: stats.dailyOrders || 0,
          deliveredItems: stats.dailySoldItems || 0,
          dailyEarnings: stats.dailyEarnings || 0
        });

        setOrderStats({
          data: {
            activeOrders: stats.activeOrders || 0,
            deliveredOrders: stats.deliveredOrders || 0,
            cancelledOrders: stats.cancelledOrders || 0
          }
        });

        setTrafficData({
          data: {
            hourlyTraffic: stats.hourlyTraffic || []
          }
        });

        setBusinessHours(stats.workingHours || null);
      }
    } catch (error) {
      console.error('Dashboard verisi yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await orderService.updateOrderStatus(orderId, newStatus);
      fetchDashboardData(); // İstatistikleri güncelle
    } catch (error) {
      console.error('Durum güncelleme hatası:', error);
    }
  };

  const handleTimeRangeChange = (newTimeRange) => {
    setSelectedTimeRange(newTimeRange);
    fetchDashboardData(newTimeRange);
  };

  const fetchRestaurantStats = async () => {
    try {
      const response = await api.get('/api/business/restaurant/stats');
      if (response.data?.success) {
        const { data } = response.data;
        setRestaurantStats({
          averageRating: data.averageRating || 0,
          totalRatings: data.totalRatings || 0,
          followersCount: data.followersCount || 0,
          followersGrowth: data.followersGrowth || 0,
          rankScore: data.rankScore || 0,
          rank: {
            position: data.rank?.position || 0,
            totalInCity: data.rank?.totalInCity || 0,
            metrics: data.rank?.metrics || {
              followers: 0,
              rating: 0,
              activity: 0
            }
          },
          recentReviews: data.recentReviews || [],
          city: data.city || ''
        });
      }
    } catch (error) {
      console.error('İşletme istatistikleri alınırken hata:', error);
      setRestaurantStats({
        averageRating: 0,
        totalRatings: 0,
        followersCount: 0,
        followersGrowth: 0,
        rankScore: 0,
        rank: {
          position: 0,
          totalInCity: 0,
          metrics: {
            followers: 0,
            rating: 0,
            activity: 0
          }
        },
        recentReviews: [],
        city: ''
      });
    }
  };

  const calculateCampaignStats = (campaignList) => {
    const now = dayjs();
    let stats = {
      total: campaignList.length,
      active: 0,
      expired: 0,
      upcoming: 0
    };

    campaignList.forEach(campaign => {
      const start = dayjs(campaign.startDate);
      const end = dayjs(campaign.endDate);

      if (now.isAfter(end)) {
        stats.expired++;
      } else if (now.isBefore(start) && campaign.isActive) {
        stats.upcoming++;
      } else if (campaign.isActive) {
        stats.active++;
      }
    });

    return stats;
  };

  const fetchCampaigns = async () => {
    try {
      const response = await campaignService.getCampaigns();
      setCampaigns(response.data);
      setCampaignStats(calculateCampaignStats(response.data));
    } catch (error) {
      console.error('Kampanya verisi alınamadı:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchWaiterCalls = async () => {
    try {
      const response = await api.get('/api/business/waiter/calls');
      if (response.data?.success && Array.isArray(response.data.data)) {
        setWaiterCalls(response.data.data);
      }
    } catch (error) {
      console.error('Garson çağrıları alınamadı:', error);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchWaiterCalls();
    const socket = io(import.meta.env.REACT_APP_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:5001', {
      transports: ['websocket']
    });

    socket.on('connect', () => {
      const restaurantId = localStorage.getItem('restaurantId');
      if (restaurantId) {
        socket.emit('join_restaurant', restaurantId);
      }
    });

    socket.on('new_order', (order) => {
      setActiveOrders(prev => [order, ...prev]);
      setDashboardStats(prev => ({
        ...prev,
        dailyOrders: prev.dailyOrders + 1
      }));
    });

    socket.on('order_status_updated', async (updatedOrder) => {
      if (updatedOrder.status === 'Teslim Edildi') {
        setActiveOrders(prev => prev.filter(order => order.id !== updatedOrder.orderId));
        
        try {
          const statsResponse = await orderService.getDashboardStats();
          if (statsResponse?.data) {
            const stats = statsResponse.data;
            
            setDashboardStats(prev => ({
              dailyOrders: prev.dailyOrders,
              deliveredItems: stats.deliveredItems || 0,
              dailyEarnings: stats.deliveredEarnings || 0
            }));

            setOrderStats({
              data: {
                activeOrders: activeOrders.length - 1,
                deliveredOrders: (stats.deliveredOrders || 0),
                cancelledOrders: stats.cancelledOrders || 0
              }
            });
          }
        } catch (error) {
          console.error('İstatistikler güncellenirken hata:', error);
        }
      } 
      else if (updatedOrder.status === 'İptal Edildi') {
        setActiveOrders(prev => prev.filter(order => order.id !== updatedOrder.orderId));
        setDashboardStats(prev => ({
          ...prev,
          dailyOrders: prev.dailyOrders
        }));
        setOrderStats(prev => ({
          data: {
            ...prev.data,
            activeOrders: prev.data.activeOrders - 1,
            cancelledOrders: prev.data.cancelledOrders + 1
          }
        }));
      }
    });

    // Garson çağrılarını dinle
    socket.on('waiter_call', () => {
      // Yeni çağrı geldiğinde son durumu API'den çek
      fetchWaiterCalls();
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    fetchRestaurantStats();
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const pricingInfo = await pricingService.getPricingInfo();
        if (pricingInfo?.success && pricingInfo?.data?.business?.plan) {
          setUserPlan(pricingInfo.data.business.plan);
        }
      } catch (error) {
        console.error('Plan bilgisi alınamadı:', error);
        // Hata durumunda localStorage'dan al
        const business = JSON.parse(localStorage.getItem('business') || '{}');
        if (business.plan) {
          setUserPlan(business.plan);
        }
      }
    };
    
    fetchPlan();
  }, []);

  useEffect(() => {
    const fetchRestaurantDetails = async () => {
      try {
        const response = await restaurantService.getRestaurantDetails();
        if (response.success && response.data) {
          // Eğer undefined veya null ise, default olarak true yap
          const menomCoinsValue = response.data.menomCoinsEnabled !== undefined && response.data.menomCoinsEnabled !== null
            ? response.data.menomCoinsEnabled 
            : true;
          setMenomCoinsEnabled(menomCoinsValue);
        }
      } catch (error) {
        console.error('Restaurant bilgisi alınamadı:', error);
        // Hata durumunda da default olarak true yap
        setMenomCoinsEnabled(true);
      }
    };
    
    fetchRestaurantDetails();
  }, []);

  const handleMenomCoinsToggle = async (enabled) => {
    const previousValue = menomCoinsEnabled;
    
    try {
      // Optimistic update - hemen state'i güncelle
      setMenomCoinsEnabled(enabled);
      
      const response = await restaurantService.updateRestaurantDetails({
        menomCoinsEnabled: enabled
      });
      
      
      // Backend'den dönen değeri kullan
      if (response && response.success && response.data) {
        const updatedValue = response.data.menomCoinsEnabled !== undefined 
          ? response.data.menomCoinsEnabled 
          : enabled;
        setMenomCoinsEnabled(updatedValue);
      } else {
        // Response formatı farklıysa, gönderdiğimiz değeri kullan
        setMenomCoinsEnabled(enabled);
      }
      
      toast({
        title: "Başarılı",
        description: enabled ? "MenomCoins kullanımı aktif edildi" : "MenomCoins kullanımı pasif edildi",
        status: "success",
        duration: 3000,
        isClosable: true
      });
    } catch (error) {
      console.error('MenomCoins güncelleme hatası:', error);
      // Hata durumunda önceki değere geri dön
      setMenomCoinsEnabled(previousValue);
      toast({
        title: "Hata",
        description: "MenomCoins ayarı güncellenirken bir hata oluştu",
        status: "error",
        duration: 3000,
        isClosable: true
      });
    }
  };

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <GettingStartedChecklist />
      <SimpleGrid
        columns={{ base: 1, md: 2, lg: 3, "2xl": 3 }}
        gap='20px'
        mb='20px'
      >
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={<Icon w='32px' h='32px' as={MdShoppingBasket} color={brandColor} />}
            />
          }
          name="Günlük Toplam Sipariş"
          value={dashboardStats.dailyOrders}
          isLoading={loading}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={<Icon w='32px' h='32px' as={MdOutlineSell} color={brandColor} />}
            />
          }
          name="Teslim Edilen Ürün"
          value={dashboardStats.deliveredItems}
          isLoading={loading}
        />
        <MiniStatistics
          startContent={
            <IconBox
              w='56px'
              h='56px'
              bg={boxBg}
              icon={<Icon w='32px' h='32px' as={MdOutlineSell} color={brandColor} />}
            />
          }
          name="Günlük Kazanç"
          value={`₺${dashboardStats.dailyEarnings}`}
          isLoading={loading}
        />
      </SimpleGrid>

      {/* MenomCoins ve Garson Çağrıları Kartları */}
      <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px" mb="20px">
      {/* MenomCoins Kontrol Kartı */}
        <Card p='20px' borderWidth="2px" borderColor={menomCoinsEnabled ? '#e45858' : 'transparent'}>
        <Flex direction='column'>
          <Flex justify='space-between' align='center' mb='20px'>
            <Flex align='center' gap={3}>
              <Icon as={MdMonetizationOn} w='24px' h='24px' color="amber.500" />
              <Text color={textColor} fontSize='lg' fontWeight='700'>
                MenomCoins Kullanımı
              </Text>
            </Flex>
          </Flex>
          <FormControl>
            <Flex align="center" justify="space-between">
              <Box>
                <FormLabel mb={1}>MenomCoins Kullanımını Aktif Et</FormLabel>
                <FormHelperText>
                  {isStaff 
                    ? 'Bu ayarı değiştirmek için yönetici yetkisine ihtiyacınız var.'
                    : 'Müşterilerinizin bu işletmede MenomCoins kullanmasına izin verin'}
                </FormHelperText>
              </Box>
              <Switch
                isChecked={menomCoinsEnabled === true}
                onChange={(e) => {
                  handleMenomCoinsToggle(e.target.checked);
                }}
                isDisabled={isStaff}
                colorScheme="amber"
                size="lg"
                key={`menom-coins-${menomCoinsEnabled}`}
                sx={{
                  'span[data-checked]': {
                    backgroundColor: '#e45858 !important',
                  },
                  'span[data-checked] span': {
                    backgroundColor: '#FFFFFF !important',
                  }
                }}
              />
            </Flex>
          </FormControl>
        </Flex>
      </Card>

        {/* Garson Çağrıları Kartı */}
        <Card p='20px'>
          <Flex direction='column'>
            <Text color={textColor} fontSize='lg' fontWeight='700' mb='12px'>
              Garson Çağrıları
            </Text>

            {waiterCalls.length === 0 ? (
              <Text fontSize="sm" color="gray.500">
                Henüz garson çağrısı bulunmuyor.
              </Text>
            ) : (
              <VStack align="stretch" spacing={2}>
                {waiterCalls.map((call, index) => {
                  const timeText = new Date(call.createdAt).toLocaleTimeString('tr-TR', {
                    hour: '2-digit',
                    minute: '2-digit',
                  });
                  const mainText = `${call.tableName} sizi çağırıyor${call.message ? `  -> \"${call.message}\"` : ''}`;

                  return (
                    <Box
                      key={call._id || index}
                      p={3}
                      borderRadius="12px"
                      bg="orange.50"
                      border="1px solid"
                      borderColor="orange.200"
                    >
                      <Flex align="center" justify="space-between" gap={3}>
                        <Box flex="1" minW={0}>
                          <Text
                            fontSize="sm"
                            fontWeight="600"
                            color="orange.800"
                          >
                            {mainText}
                          </Text>
                          <Text fontSize="xs" color="gray.500" mt={1}>
                            {timeText}
                          </Text>
                        </Box>
                        <Button
                          size="xs"
                          colorScheme="green"
                          variant="outline"
                          onClick={async () => {
                            try {
                              await api.delete(`/api/business/waiter/calls/${call._id}`);
                              setWaiterCalls(prev =>
                                prev.filter(c => c._id !== call._id)
                              );
                            } catch (error) {
                              console.error('Garson çağrısı silinirken hata:', error);
                            }
                          }}
                        >
                          Tamamlandı
                        </Button>
                      </Flex>
                    </Box>
                  );
                })}
              </VStack>
            )}
          </Flex>
        </Card>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 2, xl: 2 }} gap="20px" mb="20px">
        <ComplexTable
          tableData={activeOrders}
          isLoading={loading}
          orderStats={orderStats}
          onStatusChange={handleStatusChange}
          onTimeRangeChange={handleTimeRangeChange}
        />
        <DailyTraffic 
          trafficData={trafficData}
          isLoading={loading}
          timeRange={selectedTimeRange}
          businessHours={businessHours}
        />
      </SimpleGrid>
      <SimpleGrid
        columns={{ base: 1, md: 1, lg: 2}}
        gap="20px"
        mb="20px"
        alignItems="stretch"
      >
        {userPlan === 'PREMIUM' ? (
          <Tasks />
        ) : (
          <Card p='20px'>
            <Flex direction='column' height="100%" justify="center" align="center" textAlign="center">
              <Icon as={MdOutlineLocalOffer} w='48px' h='48px' color={brandColor} mb='16px' />
              <Text color={textColor} fontSize='lg' fontWeight='700' mb='8px'>
                Görev Yönetimi
              </Text>
              <Text color="gray.500" fontSize='sm' mb='16px'>
                Görev yönetimi özelliğine erişmek için PREMIUM plana yükseltin.
              </Text>
              <Button 
                colorScheme="brand" 
                size="sm" 
                width="100%"
                onClick={() => setIsUpgradeModalOpen(true)}
              >
                PREMIUM'a Yükselt
              </Button>
            </Flex>
          </Card>
        )}
        {/*  Kampanyalar Kartı */}
        <Card p='20px'>
          <Flex direction='column' height="100%">
            <Flex justify='space-between' align='center' mb='20px'>
              <Text color={textColor} fontSize='lg' fontWeight='700'>
                Kampanyalar
              </Text>
            </Flex>

            {isLoading ? (
              <Flex justify="center" py={4}>
                <Spinner />
              </Flex>
            ) : (
              <VStack spacing={4} align="stretch">
                {/* Kampanya İstatistikleri */}
                <SimpleGrid columns={2} spacing={4}>
                  <Box p={5} bg="gray.50" borderRadius="md">
                    <Text fontSize="sm" color="gray.600">Toplam</Text>
                    <Text fontSize="2xl" fontWeight="bold" color={textColor}>
                      {campaignStats.total}
                    </Text>
                  </Box>
                  <Box p={5} bg="green.50" borderRadius="md">
                    <Text fontSize="sm" color="green.600">Aktif</Text>
                    <Text fontSize="2xl" fontWeight="bold" color="green.600">
                      {campaignStats.active}
                    </Text>
                  </Box>
                  <Box p={5} bg="blue.50" borderRadius="md">
                    <Text fontSize="sm" color="blue.600">Yaklaşan</Text>
                    <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                      {campaignStats.upcoming}
                    </Text>
                  </Box>
                  <Box p={5} bg="red.50" borderRadius="md">
                    <Text fontSize="sm" color="red.600">Süresi Dolan</Text>
                    <Text fontSize="2xl" fontWeight="bold" color="red.600">
                      {campaignStats.expired}
                    </Text>
                  </Box>
                </SimpleGrid>

                {/* Aktif Kampanyalar Listesi
                <Box maxH="200px" overflowY="auto">
                  <VStack spacing={2} align="stretch">
                    {campaigns
                      .filter(campaign => {
                        const now = dayjs();
                        const start = dayjs(campaign.startDate);
                        const end = dayjs(campaign.endDate);
                        return campaign.isActive && 
                               now.isAfter(start) && 
                               now.isBefore(end);
                      })
                      .map((campaign) => (
                        <Box 
                          key={campaign._id} 
                          p={2} 
                          borderWidth="1px" 
                          borderRadius="md"
                          bg="white"
                        >
                          <HStack justify="space-between">
                            <Text fontSize="sm" fontWeight="medium">
                              {campaign.name}
                            </Text>
                            <Badge colorScheme="green">
                              %{campaign.discount}
                            </Badge>
                          </HStack>
                        </Box>
                      ))}
                  </VStack>
                </Box> 
                */}
              </VStack>
            )}
            <Link to={`/admin/${getBusinessSlug() || 'panel'}/kampanyalar`} style={{ position: 'sticky', top: 0, width: '100%', marginTop: '20px' }}>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    width="100%"
                    color={brandColor}
                    borderColor={brandColor}
                  >
                    Kampanyalarını Yönet
                  </Button>
                </Link>
          </Flex>
        </Card>
      </SimpleGrid>
      <SimpleGrid columns={{ base: 1, md: 1, xl: 2 }} gap="20px" mb="10px">
        <SimpleGrid columns={{ base: 1, md: 2, xl: 2 }} gap="20px"></SimpleGrid>
      </SimpleGrid>
      
      <PremiumUpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        onConfirm={async () => {
          // Plan bilgisini güncelle
          const pricingInfo = await pricingService.getPricingInfo();
          if (pricingInfo?.success && pricingInfo?.data?.business?.plan) {
            setUserPlan(pricingInfo.data.business.plan);
          }
        }}
      />
    </Box>
  );
}
